import { useState } from 'react';

import type { Suggestion } from '../../aiSuggestionsPluginType';

/**
 * Status of a suggestion's regeneration lifecycle, derived by
 * `useRegenerationStatus` and rendered by `StatusBanner`.
 * - `regenerating`: a stale check + regenerate request is in flight.
 * - `updated`: regeneration just finished successfully.
 * - `stale`: the suggestion is confirmed out of date.
 */
export type RegenerationStatus = 'regenerating' | 'stale' | 'updated';

type BannerTrackingState = {
	id: string | undefined;
	isJustUpdated: boolean;
	isRegenerating: boolean;
};

export const useRegenerationStatus = (
	suggestion: Suggestion | undefined,
	isRegenerating: boolean,
): RegenerationStatus | undefined => {
	const id = suggestion?.id;
	const isStale = suggestion?.status === 'STALE';

	const [tracking, setTracking] = useState<BannerTrackingState>({
		id,
		isRegenerating,
		isJustUpdated: false,
	});

	const isSameSuggestion = tracking.id === id;
	const justFinishedRegenerating = isSameSuggestion && tracking.isRegenerating && !isRegenerating;
	const isJustUpdated = justFinishedRegenerating
		? !isStale
		: isSameSuggestion && tracking.isJustUpdated;

	if (
		tracking.id !== id ||
		tracking.isRegenerating !== isRegenerating ||
		tracking.isJustUpdated !== isJustUpdated
	) {
		setTracking({ id, isRegenerating, isJustUpdated });
	}

	if (isRegenerating) {
		return 'regenerating';
	}
	if (isJustUpdated) {
		return 'updated';
	}
	if (isStale) {
		return 'stale';
	}
	return undefined;
};
